import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Trash2, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function CleanupTestUsersPanel() {
  const [data, setData] = useState({ count: 0, users: [] });
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/test-users`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error("Impossible de charger la liste des comptes test.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/test-users/delete`, {}, { withCredentials: true });
      toast.success(`${res.data.deleted_users} compte(s) supprimé(s).`);
      setConfirm(false);
      await load();
    } catch (e) {
      toast.error("Erreur pendant la suppression.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#C76542]/30 rounded-2xl bg-gradient-to-br from-[#FFF4F0] to-[#F5EFE5]" data-testid="cleanup-test-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="w-4 h-4 text-[#C76542]" strokeWidth={1.8} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#C76542] font-semibold">Nettoyage</span>
            </div>
            <h3 className="font-heading text-lg text-[#493329]">Comptes de test à supprimer</h3>
            <p className="text-xs text-[#7A6455] mt-1">
              {data.count > 0
                ? "Vérifiez la liste ci-dessous avant de confirmer. Les vraies clientes ne sont jamais inclues."
                : "Aucun compte test détecté. Votre base est propre."}
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full text-xs h-8 border-[#E9DDCC] text-[#7A6455]">
            Actualiser
          </Button>
        </div>

        {data.count === 0 ? (
          <div className="flex items-center gap-2 bg-white rounded-xl p-4 border border-[#E9DDCC] text-sm text-[#7A6455]">
            <CheckCircle2 className="w-4 h-4 text-[#7C9C82]" />
            Aucun compte test à supprimer.
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-[#E9DDCC] max-h-[300px] overflow-y-auto">
              <ul className="divide-y divide-[#EFE4D0] text-sm">
                {data.users.map((u, i) => (
                  <li key={i} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-[#493329] font-medium truncate max-w-[40%]">{u.name || '—'}</span>
                    <span className="text-[#7A6455] text-xs ml-3 truncate">{u.email}</span>
                  </li>
                ))}
              </ul>
            </div>

            {!confirm ? (
              <Button
                onClick={() => setConfirm(true)}
                className="w-full bg-[#C76542] hover:bg-[#493329] text-white rounded-xl h-12 transition-all duration-300"
                data-testid="cleanup-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer ces {data.count} compte{data.count > 1 ? 's' : ''} de test
              </Button>
            ) : (
              <div className="bg-[#FFF8E6] border border-[#C76542]/40 rounded-xl p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-[#C76542] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#493329]">
                    Confirmez la suppression définitive de <strong>{data.count}</strong> compte{data.count > 1 ? 's' : ''} de test ?
                    <br/>
                    <span className="text-xs text-[#7A6455]">Cette action est irréversible. Les vraies clientes ne sont pas concernées.</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setConfirm(false)}
                    variant="outline"
                    className="flex-1 rounded-xl h-10 border-[#E9DDCC] text-[#7A6455]"
                    disabled={deleting}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={doDelete}
                    className="flex-1 bg-[#C76542] hover:bg-[#493329] text-white rounded-xl h-10"
                    disabled={deleting}
                    data-testid="cleanup-confirm-delete"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Oui, supprimer'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
